"use client"

import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Award, Plus, Search, StickyNote, TrendingUp, Loader2, Copy, Zap } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useExercises } from '@/context/ExerciseContext';
import { useSound } from "@/hooks/use-sound";
import { triggerHapticFeedback } from "@/lib/haptics";
import { cn } from "@/lib/utils";
import { exerciseLoggerSchema, type ExerciseLoggerFormData, transformExerciseFormData } from "@/lib/validationSchemas";
import { usePostHog } from 'posthog-js/react';

interface ExerciseLoggerProps {
  onNotification: (message: string, type: 'success' | 'error' | 'pr', exerciseData?: any) => void;
  soundEnabled?: boolean;
  isTransitioning?: boolean;
  templateMode?: {
    type: 'upperA' | 'upperB' | 'lowerA' | 'lowerB' | 'fullBodyA' | 'fullBodyB';
    exercises: Array<{
      primary: string;
      alternatives: string[];
      sets: number;
      target: string;
    }>;
  };
}

export default function ExerciseLogger({ onNotification, soundEnabled = false, isTransitioning = false, templateMode }: ExerciseLoggerProps) {
  const { createExercise, getLastSession, getAllExerciseNames, createCustomExercise, getExerciseProgress, getUniqueUsedExercises, loading } = useExercises();
  const posthog = usePostHog();
  
  // Form state with validation
  const form = useForm<ExerciseLoggerFormData>({
    resolver: zodResolver(exerciseLoggerSchema),
    mode: 'onChange',
    defaultValues: {
      selectedExercise: templateMode?.exercises[0]?.primary || '', // Pre-populate with template exercise
      weight: '',
      reps: '',
      sets: '',
      notes: ''
    }
  });
  
  const { control, handleSubmit, setValue, watch, reset } = form;

  // Watch form values for real-time updates
  const watchedValues = watch();
  const { selectedExercise, weight } = watchedValues;

  // Helper function to pre-fill form with last session data or defaults
  const preFillFormData = useCallback((exerciseName: string, defaultSets?: number) => {
    const lastSession = getLastSession(exerciseName);
    if (lastSession) {
      setValue('weight', lastSession.weight.toString(), { shouldValidate: true });
      setValue('reps', lastSession.reps.toString(), { shouldValidate: true });
      setValue('sets', lastSession.sets.toString(), { shouldValidate: true });
    } else {
      setValue('weight', '', { shouldValidate: true });
      setValue('reps', '', { shouldValidate: true });
      setValue('sets', defaultSets ? defaultSets.toString() : '', { shouldValidate: true });
    }
  }, [setValue, getLastSession]);

  // Pre-fill template exercise data when templateMode is set
  React.useEffect(() => {
    if (templateMode?.exercises[0]) {
      const templateExercise = templateMode.exercises[0];
      setValue('selectedExercise', templateExercise.primary, { shouldValidate: true });
      
      // Pre-fill with last session data or template defaults
      preFillFormData(templateExercise.primary, templateExercise.sets);
    }
  }, [templateMode, setValue, getLastSession, preFillFormData]);

  // Reset applied progression when exercise changes
  React.useEffect(() => {
    setAppliedProgression(false);
  }, [selectedExercise]);

  // UI state (unchanged)
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [customExerciseName, setCustomExerciseName] = useState('');
  const [customExerciseOpen, setCustomExerciseOpen] = useState(false);
  const [showPRCelebration, setShowPRCelebration] = useState(false);
  const [appliedProgression, setAppliedProgression] = useState(false);

  // Sound effects
  const playAddSound = useSound("https://blob.v0.dev/sounds/click.mp3", { soundEnabled, volume: 0.7 });
  const playPRSound = useSound("https://blob.v0.dev/sounds/chime.mp3", { soundEnabled, volume: 0.5 });

  const allExercises = getAllExerciseNames();
  const uniqueUsedExercises = getUniqueUsedExercises();
  
  // Template exercises (if in template mode)
  const templateExercises = templateMode ? templateMode.exercises.map(ex => ex.primary) : [];
  const templateAlternatives = templateMode ? templateMode.exercises.flatMap(ex => ex.alternatives) : [];

  // Create a map of exercise names to their usage info for quick lookup
  const usedExerciseMap = new Map(uniqueUsedExercises.map(ex => [ex.name, ex]));

  // Filter exercises based on search query
  const filterExercises = (exercises: string[]) => {
    if (!searchQuery.trim()) return exercises;
    return exercises.filter(exercise => 
      exercise.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const filteredTemplateExercises = templateMode ? filterExercises(templateExercises) : [];
  const filteredTemplateAlternatives = templateMode ? filterExercises(templateAlternatives.filter(alt => !templateExercises.includes(alt))) : [];
  
  // Get all unique used exercises that aren't template exercises
  const filteredUsedExercises = filterExercises(
    uniqueUsedExercises
      .filter(ex => !templateExercises.includes(ex.name) && !templateAlternatives.includes(ex.name))
      .map(ex => ex.name)
  );
  
  // Get all exercises that haven't been used yet
  const unusedExercises = allExercises.filter(ex => 
    !usedExerciseMap.has(ex) && 
    !templateExercises.includes(ex) && 
    !templateAlternatives.includes(ex)
  );
  const filteredUnusedExercises = filterExercises(unusedExercises);


  const handleExerciseSelect = (exercise: string) => {
    setValue('selectedExercise', exercise, { shouldValidate: true });
    setShowDropdown(false);
    setSearchQuery(''); // Clear search when exercise is selected
    
    // Check if this is a template exercise to pre-fill sets
    const templateExercise = templateMode?.exercises.find(ex => 
      ex.primary === exercise || ex.alternatives.includes(exercise)
    );
    
    // Pre-fill with last session data or template defaults
    preFillFormData(exercise, templateExercise?.sets);
    setValue('notes', '', { shouldValidate: true });
    
    triggerHapticFeedback("light");
    playAddSound();
  };

  const handleCreateCustomExercise = async () => {
    if (!customExerciseName.trim()) {
      onNotification('Please enter an exercise name', 'error');
      return;
    }

    const result = await createCustomExercise({ name: customExerciseName.trim() });
    
    if (result.success) {
      const exerciseName = result.data?.name || customExerciseName.trim();
      onNotification(`✅ ${result.message || 'Custom exercise created successfully!'}`, 'success');
      // Automatically select the new exercise
      handleExerciseSelect(exerciseName);
      setCustomExerciseOpen(false);
      setCustomExerciseName('');
      triggerHapticFeedback("success");
    } else {
      onNotification(`❌ ${result.error || 'Failed to create custom exercise. Please try again.'}`, 'error');
    }
  };


  const onSubmit = async (data: ExerciseLoggerFormData) => {
    const exerciseData = transformExerciseFormData(data);
    const result = await createExercise(exerciseData);

    if (result.success) {
      // Hide progression immediately to prevent flash during form reset
      setAppliedProgression(true);

      // Track exercise logging in PostHog
      posthog.capture('exercise_logged', {
        exercise: exerciseData.exercise,
        is_pr: result.isPersonalRecord || false
      });

      // Enhanced PR celebration with v0 polish
      if (result.isPersonalRecord) {
        setShowPRCelebration(true);
        setTimeout(() => setShowPRCelebration(false), 2500);
        onNotification('Personal record achieved', 'pr', exerciseData);
        playPRSound();
        triggerHapticFeedback("success");
      } else {
        onNotification('Exercise logged successfully', 'success', exerciseData);
        playAddSound();
        triggerHapticFeedback("light");
      }

      // Reset form
      reset();
    } else {
      onNotification('❌ Failed to log exercise. Please try again.', 'error');
    }
  };

  // Check if exercise has notes in last session
  const exerciseHasNotes = (exerciseName: string) => {
    const session = getLastSession(exerciseName);
    return session?.notes && session.notes.trim().length > 0;
  };

  // Progressive overload analysis - evidence-aligned double progression with safety checks
  const analyzeProgressionOpportunity = (exerciseName: string) => {
    const allExerciseProgress = getExerciseProgress();
    const exerciseHistory = allExerciseProgress[exerciseName] as Array<{ date: string; weight: number; timestamp: string; sets: number; reps: number }> | undefined;

    if (!exerciseHistory || exerciseHistory.length < 2) {
      return null; // Need at least 2 sessions to analyze
    }

    // Helpers
    const isLowerBody = (name: string) => {
      const lowerKeywords = ['leg', 'squat', 'deadlift', 'calf', 'hamstring', 'glute', 'quad'];
      const n = name.toLowerCase();
      return lowerKeywords.some(k => n.includes(k));
    };

    const roundToQuarterKg = (value: number) => Math.ceil(value * 4) / 4;

    const minIncrementKg = 0.5; // practical microload

    const estimate1RM = (weight: number, reps: number) => {
      // Epley: 1RM = w * (1 + reps/30)
      return weight * (1 + reps / 30);
    };

    const containsPainFlag = (notes?: string | null) => {
      if (!notes) return false;
      const n = notes.toLowerCase();
      return /(pain|hurt|injur|sharp|twinge|pinch|aggravate|flare)/.test(n);
    };

    // Build recent (latest first)
    const recentSessions = [...exerciseHistory]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 4);

    const lastSession = recentSessions[0];
    const previousSession = recentSessions[1];

    // Safety: avoid pushing load if notes indicate pain
    const lastNotes = getLastSession(exerciseName)?.notes;
    if (containsPainFlag(lastNotes)) {
      return null;
    }

    // Compute simple plateau signals
    const sameWeightStreak = recentSessions.reduce((streak, s, idx, arr) => {
      if (idx === 0) return 1;
      return s.weight === arr[idx - 1].weight ? streak + 1 : streak;
    }, 0);

    const last1RM = estimate1RM(lastSession.weight, lastSession.reps);
    const prev1RM = previousSession ? estimate1RM(previousSession.weight, previousSession.reps) : last1RM;
    const oneRmChangePct = previousSession ? (last1RM - prev1RM) / Math.max(prev1RM, 1) : 0;

    // Rep targets for double progression
    const repLower = 8;
    const repUpper = 12;

    // Category-based increment (typical: upper 2.5–5%, lower 5–10%) -> use conservative midpoint
    const incPct = isLowerBody(exerciseName) ? 0.07 : 0.035;

    // Decision logic
    // 1) If at top of rep range and weight has been constant ≥2 sessions -> increase load
    if (sameWeightStreak >= 2 && lastSession.reps >= repUpper) {
      const rawNext = lastSession.weight * (1 + incPct);
      // Handle negative weights (assisted exercises) differently
      const increment = lastSession.weight < 0 ? -minIncrementKg : minIncrementKg;
      const rounded = roundToQuarterKg(Math.max(rawNext, lastSession.weight + increment));
      return {
        type: 'weight' as const,
        current: lastSession.weight,
        suggested: rounded,
        reason: `Top of rep range reached. Increase load ${isLowerBody(exerciseName) ? '~7%' : '~3.5%'} (double progression)`
      };
    }

    // 2) Within rep range -> add 1 rep until upper, then load
    if (lastSession.reps >= repLower && lastSession.reps < repUpper) {
      return {
        type: 'reps' as const,
        current: lastSession.reps,
        suggested: Math.min(lastSession.reps + 1, repUpper),
        reason: 'Progress reps toward upper bound (double progression)'
      };
    }

    // 3) Below rep range. If weight has been unchanged ≥2 sessions and 1RM isn't improving -> consider small load bump, else build reps first
    if (lastSession.reps < repLower) {
      if (sameWeightStreak >= 2 && oneRmChangePct < 0.01) {
        const rawNext = lastSession.weight * (1 + (isLowerBody(exerciseName) ? 0.05 : 0.025));
        // Handle negative weights (assisted exercises) differently
        const increment = lastSession.weight < 0 ? -minIncrementKg : minIncrementKg;
        const rounded = roundToQuarterKg(Math.max(rawNext, lastSession.weight + increment));
        return {
          type: 'weight' as const,
          current: lastSession.weight,
          suggested: rounded,
          reason: 'Load stagnant and 1RM plateau. Apply conservative load increase.'
        };
      }
      return {
        type: 'reps' as const,
        current: lastSession.reps,
        suggested: Math.min(lastSession.reps + 1, repLower),
        reason: 'Below target reps. Build reps before adding load.'
      };
    }

    // 4) If none of the above, check 1RM trend; if plateau across last 2 sessions and reps at upper -> load increase
    if (oneRmChangePct < 0.01 && lastSession.reps >= repUpper) {
      const rawNext = lastSession.weight * (1 + incPct);
      // Handle negative weights (assisted exercises) differently
      const increment = lastSession.weight < 0 ? -minIncrementKg : minIncrementKg;
      const rounded = roundToQuarterKg(Math.max(rawNext, lastSession.weight + increment));
      return {
        type: 'weight' as const,
        current: lastSession.weight,
        suggested: rounded,
        reason: 'Estimated 1RM plateau. Increase load.'
      };
    }

    return null; // No clear progression suggestion
  };

  const lastSession = selectedExercise ? getLastSession(selectedExercise) : null;
  const wouldBePR = selectedExercise && weight && lastSession 
    ? (lastSession.weight < 0 
        ? parseFloat(weight.toString()) > lastSession.weight // For assisted exercises, less negative = better
        : parseFloat(weight.toString()) > lastSession.weight) // For regular exercises, more weight = better
    : false;
  
  // Get progression recommendation
  const progressionRecommendation = selectedExercise ? analyzeProgressionOpportunity(selectedExercise) : null;

  // Apply progression suggestion
  const applyProgression = () => {
    if (!progressionRecommendation) return;
    
    if (progressionRecommendation.type === 'weight') {
      setValue('weight', progressionRecommendation.suggested.toString(), { shouldValidate: true });
      triggerHapticFeedback("light");
    } else if (progressionRecommendation.type === 'reps') {
      setValue('reps', progressionRecommendation.suggested.toString(), { shouldValidate: true });
      triggerHapticFeedback("light");
    }
    
    // Hide the progression box after applying
    setAppliedProgression(true);
  };

  // Simple button text - no longer need to indicate what's missing
  const getButtonText = (): string => {
    if (loading) return 'Adding to workout...';
    if (isTransitioning) return 'Moving to next exercise...';
    return 'Add to workout';
  };

  return (
    <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
      <Card className="border-border bg-card/50 backdrop-blur-sm overflow-hidden">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Log Exercise</CardTitle>
          {templateMode && (
            <Badge variant="secondary" className="text-xs">
              {templateMode.type.charAt(0).toUpperCase() + templateMode.type.slice(1)} Template
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Enhanced PR Celebration with v0 animations */}
          <AnimatePresence>
            {showPRCelebration && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: -20 }}
                animate={{ 
                  opacity: 1, 
                  scale: 1, 
                  y: 0,
                }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                transition={{ duration: 0.5, type: "spring", bounce: 0.4 }}
                className="bg-gradient-to-r from-amber-50/80 to-orange-50/60 dark:from-amber-950/30 dark:to-orange-950/20 border border-amber-200 dark:border-amber-800/50 rounded-lg p-4 text-center relative overflow-hidden"
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.02, 1]
                  }}
                  transition={{ 
                    duration: 0.8, 
                    repeat: 1,
                    repeatType: "reverse"
                  }}
                  className="flex items-center justify-center gap-3 text-foreground font-semibold text-lg"
                >
                  <Award className="w-5 h-5 text-amber-500" />
                  <span>Personal Record</span>
                </motion.div>
                <div className="text-muted-foreground mt-1 text-sm">Performance milestone achieved</div>
                
                {/* Subtle accent dots */}
                <div className="absolute inset-0 pointer-events-none">
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ 
                        opacity: [0, 0.4, 0], 
                        scale: [0, 1, 1.2]
                      }}
                      transition={{ 
                        duration: 1.5, 
                        delay: i * 0.2,
                        ease: "easeOut"
                      }}
                      className="absolute top-1/4 left-1/4 w-1 h-1 bg-amber-400 rounded-full"
                      style={{ 
                        left: `${25 + i * 25}%`,
                        top: `${20 + i * 10}%`
                      }}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Exercise Selection - v0 styled dropdown */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-1">
              Exercise 
              <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  const newState = !showDropdown;
                  setShowDropdown(newState);
                  if (!newState) setSearchQuery(''); // Clear search when closing dropdown
                }}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-3 text-left bg-background border rounded-lg hover:bg-accent/50 transition-colors focus:ring-2 focus:ring-primary focus:border-primary",
                  templateMode && selectedExercise === templateMode.exercises[0]?.primary
                    ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20" 
                    : "border-border"
                )}
              >
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "text-sm truncate",
                    selectedExercise ? "text-foreground font-medium" : "text-muted-foreground"
                  )}>
                    {selectedExercise || "Select an exercise"}
                  </span>
                  {templateMode && selectedExercise === templateMode.exercises[0]?.primary && (
                    <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                      Template
                    </Badge>
                  )}
                </div>
                <ChevronDown className={cn(
                  "w-4 h-4 text-muted-foreground transition-transform duration-200",
                  showDropdown && "transform rotate-180"
                )} />
              </button>

              <AnimatePresence>
                {showDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg z-50 max-h-64 overflow-hidden"
                  >
                    {/* Search Input */}
                    <div className="p-3 border-b border-border bg-muted/20">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Search exercises..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 h-8 text-sm"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="max-h-48 overflow-y-auto">
                      {/* Add Custom Exercise Option */}
                    <Dialog open={customExerciseOpen} onOpenChange={setCustomExerciseOpen}>
                      <DialogTrigger asChild>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-accent/50 transition-colors flex items-center gap-2 text-green-600 font-medium border-b border-border"
                        >
                          <Plus className="w-4 h-4" />
                          Add Custom Exercise
                        </button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Create Custom Exercise</DialogTitle>
                          <DialogDescription>
                            Add a new exercise to your personal library.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label htmlFor="customName" className="text-right">
                              Exercise Name
                            </Label>
                            <div className="mt-2">
                              <Input
                                id="customName"
                                value={customExerciseName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomExerciseName(e.target.value)}
                                placeholder="e.g., shoulder press machine"
                              />
                            </div>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setCustomExerciseOpen(false)}>
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleCreateCustomExercise}
                            disabled={loading || !customExerciseName.trim()}
                          >
                            {loading ? 'Creating...' : 'Create & Select'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    {/* Template Primary Exercises */}
                    {templateMode && filteredTemplateExercises.length > 0 && (
                      <div>
                        <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-gradient-to-r from-blue-50/50 to-indigo-50/30 dark:from-blue-950/20 dark:to-indigo-950/10 border-b border-blue-200/50 dark:border-blue-800/30">
                          {templateMode.type.charAt(0).toUpperCase() + templateMode.type.slice(1)} Template
                        </div>
                        {filteredTemplateExercises.map((exercise) => {
                          const templateEx = templateMode.exercises.find(ex => ex.primary === exercise);
                          return (
                            <button
                              key={exercise}
                              type="button"
                              onClick={() => handleExerciseSelect(exercise)}
                              className="w-full px-3 py-2 text-left hover:bg-accent/50 transition-colors flex items-center justify-between"
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="text-sm font-medium truncate">{exercise}</span>
                                {exerciseHasNotes(exercise) && (
                                  <StickyNote className="w-3 h-3 text-blue-500 opacity-70" />
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Badge variant="default" className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                                  {templateEx?.sets} sets
                                </Badge>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Template Alternative Exercises */}
                    {templateMode && filteredTemplateAlternatives.length > 0 && (
                      <div>
                        <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/30 border-b border-border">
                          Alternative Options
                        </div>
                        {filteredTemplateAlternatives.map((exercise) => (
                          <button
                            key={exercise}
                            type="button"
                            onClick={() => handleExerciseSelect(exercise)}
                            className="w-full px-3 py-2 text-left hover:bg-accent/50 transition-colors flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="text-sm truncate">{exercise}</span>
                              {exerciseHasNotes(exercise) && (
                                <StickyNote className="w-3 h-3 text-blue-500 opacity-70" />
                              )}
                            </div>
                            <Badge variant="outline" className="text-xs">Alternative</Badge>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Recently Used */}
                    {filteredUsedExercises.length > 0 && (
                      <div>
                        <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/30 border-b border-border">
                          Previously Used Exercises
                        </div>
                        {filteredUsedExercises.map((exercise) => {
                          const usageInfo = usedExerciseMap.get(exercise);
                          const daysAgoText = usageInfo?.daysAgo === 0 
                            ? "today" 
                            : usageInfo?.daysAgo === 1 
                            ? "yesterday" 
                            : `${usageInfo?.daysAgo} days ago`;
                          
                          return (
                            <button
                              key={exercise}
                              type="button"
                              onClick={() => handleExerciseSelect(exercise)}
                              className="w-full px-3 py-2 text-left hover:bg-accent/50 transition-colors flex items-center justify-between"
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="text-sm truncate">{exercise}</span>
                                {exerciseHasNotes(exercise) && (
                                  <StickyNote className="w-3 h-3 text-blue-500 opacity-70" />
                                )}
                              </div>
                              <Badge variant="secondary" className="text-xs">
                                Used {daysAgoText}
                              </Badge>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* All Exercises */}
                    {filteredUnusedExercises.length > 0 && (
                      <div>
                        <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/30 border-b border-border">
                          All Available Exercises
                        </div>
                        {filteredUnusedExercises.map((exercise) => (
                          <button
                            key={exercise}
                            type="button"
                            onClick={() => handleExerciseSelect(exercise)}
                            className="w-full px-3 py-2 text-left hover:bg-accent/50 transition-colors flex items-center gap-2"
                          >
                            <span className="text-sm truncate">{exercise}</span>
                            {exerciseHasNotes(exercise) && (
                              <StickyNote className="w-3 h-3 text-blue-500 opacity-70" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* No Results */}
                    {searchQuery.trim() && 
                     filteredUsedExercises.length === 0 && 
                     filteredUnusedExercises.length === 0 && 
                     filteredTemplateExercises.length === 0 &&
                     filteredTemplateAlternatives.length === 0 && (
                      <div className="px-3 py-4 text-center text-muted-foreground text-sm">
                        No exercises found for "{searchQuery}"
                        <div className="text-xs mt-1">Try creating a custom exercise instead</div>
                      </div>
                    )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Last Session Reference - Enhanced v0 styling */}
          <AnimatePresence>
            {selectedExercise && lastSession && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="bg-gradient-to-br from-muted/30 to-muted/10 border border-border rounded-lg p-5 backdrop-blur-sm"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm font-medium text-muted-foreground">Last Session</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(lastSession.timestamp).toLocaleDateString()}
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold tracking-tight">{lastSession.weight}</div>
                    <div className="text-xs text-muted-foreground font-medium">kg</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold tracking-tight">{lastSession.reps}</div>
                    <div className="text-xs text-muted-foreground font-medium">reps</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold tracking-tight">{lastSession.sets}</div>
                    <div className="text-xs text-muted-foreground font-medium">sets</div>
                  </div>
                </div>
                
                {lastSession.notes && (
                  <div className="bg-background/60 rounded-md p-3 border border-border/50">
                    <div className="text-sm text-muted-foreground leading-relaxed">
                      {lastSession.notes}
                    </div>
                  </div>
                )}

                {progressionRecommendation && !appliedProgression && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className="bg-gradient-to-r from-green-50/50 to-emerald-50/30 dark:from-green-950/20 dark:to-emerald-950/10 border border-green-200/50 dark:border-green-800/30 rounded-md p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                          <span className="text-sm font-medium text-green-700 dark:text-green-300">
                            Progression Ready
                          </span>
                        </div>
                        <div className="text-xs text-green-600 dark:text-green-400 leading-relaxed mb-2">
                          {progressionRecommendation.reason}
                        </div>
                        <div className="text-sm font-semibold text-green-800 dark:text-green-200">
                          Try: {progressionRecommendation.suggested}
                          {progressionRecommendation.type === 'weight' ? 'kg' : ' reps'}
                          {progressionRecommendation.type === 'weight' && 
                            ` (+${(progressionRecommendation.suggested - progressionRecommendation.current).toFixed(2)}kg)`
                          }
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={applyProgression}
                        className="h-7 px-2 text-xs border-green-200 dark:border-green-800/50 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-950/20"
                      >
                        Apply
                      </Button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Input Fields - v0 layout with validation */}
          <div className="grid grid-cols-3 gap-4">
            <FormField
              control={control}
              name="weight"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1">
                    Weight (kg) 
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      step="0.25"
                      placeholder="25"
                      className="h-9 text-center font-mono"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="reps"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1">
                    Reps 
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      placeholder="8"
                      className="h-9 text-center font-mono"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="sets"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1">
                    Sets 
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      placeholder="3"
                      className="h-9 text-center font-mono"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* PR Detection - Enhanced */}
          <AnimatePresence>
            {wouldBePR && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3, type: "spring" }}
                className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-300 rounded-lg p-3"
              >
                <div className="flex items-center gap-2 text-yellow-800">
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                  >
                    <Award className="w-5 h-5 text-yellow-600" />
                  </motion.div>
                  <span className="font-medium">Personal record potential</span>
                  <Zap className="w-4 h-4 text-orange-500" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Notes */}
          <FormField
            control={control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Notes (optional)</FormLabel>
                  {selectedExercise && lastSession?.notes && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setValue('notes', lastSession.notes || '', { shouldValidate: true });
                        triggerHapticFeedback("light");
                        playAddSound();
                      }}
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" />
                      Copy from last session
                    </Button>
                  )}
                </div>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    placeholder="Felt strong today"
                    className="h-9"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />


          {/* Submit Button - Enhanced with v0 motion */}
          <motion.div whileTap={{ scale: 0.98 }}>
            <Button
              type="submit"
              disabled={loading || isTransitioning}
              className="w-full h-10 font-medium"
            >
              {loading || isTransitioning ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              {getButtonText()}
            </Button>
          </motion.div>
          </form>
        </Form>
      </CardContent>
    </Card>
    </motion.div>
  );
}